import { useNavigate, useRouter } from "@tanstack/react-router";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import * as Sentry from "@sentry/react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

type GeneralErrorProps = React.HTMLAttributes<HTMLDivElement> & {
  minimal?: boolean;
  error?: unknown;
};

export function GeneralError({
  className,
  minimal = false,
  error,
}: GeneralErrorProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { history } = useRouter();

  // ALWAYS report when 500 page is shown, even if error object is missing
  // This ensures every 500 page display = Sentry report
  // If no error object: create synthetic error so we know page was shown
  useEffect(() => {
    if (import.meta.env.VITE_SENTRY_DSN) {
      const errorToReport =
        error || new Error("500 page displayed without error context");

      Sentry.captureException(errorToReport, {
        tags: {
          context: "general-error-component",
          source: "router-or-direct-render",
          hasErrorObject: !!error, // Track if actual error was provided
        },
        extra: {
          minimal,
          url: window.location.href,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          referrer: document.referrer,
        },
        level: error ? "error" : "warning", // Warning level for synthetic errors
      });
    }
  }, [error, minimal]);

  return (
    <div className={cn("h-svh w-full", className)}>
      <div className="m-auto flex h-full w-full flex-col items-center justify-center gap-2">
        {!minimal && (
          <h1 className="text-[7rem] leading-tight font-bold">500</h1>
        )}
        <span className="font-medium">{t("errors.pages.generalTitle")}</span>
        <p className="text-muted-foreground max-w-sm text-center">
          {t("errors.pages.generalDescription")}
        </p>
        {!minimal && (
          <div className="mt-6 flex gap-4">
            <Button variant="outline" onClick={() => history.go(-1)}>
              {t("errors.pages.goBack")}
            </Button>
            <Button onClick={() => navigate({ to: "/" })}>
              {t("errors.pages.backToHome")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
